import axios from 'axios'


//#region configuration
class MiddlewareConnector {
    /** @type{string} */
    _middlewareUrl

    constructor() {
        this._middlewareUrl = window?.configs?.['VITE_MIDDLEWARE_URL'] || import.meta.env.VITE_MIDDLEWARE_URL
    }
    /**
     * Get configuration
     * @returns {Promise<object>} configuration
     */
    getConfiguration = async () => {
        let url = this._middlewareUrl
        url += '/configuration'

        return axios.get(url)
        .then(response => response.data)
    }

    /**
     * Get required fields
     * @returns {Promise<object>} required fields
     */
    getRequiredFields = async () => {
        let url = this._middlewareUrl
        url += '/configuration/required-fields'
        return axios.get(url)
        .then(response => response.data)
    }

    /**
     * Get base data
     * @returns {Promise<object>} base data
     */
    getBaseData = async () => {
        let url = this._middlewareUrl
        url += '/configuration/base-data'

        return axios.get(url)
        .then(response => response.data)
    }

    /**
     * Returns possible dates to which a scheduled school change is possible
     * @param {string} uuidSchoolYear uuid of the current school year
     * @returns {Promise<string[]>} Possible dates to which a scheduled school change is possible
     */
    getScheduledSchoolChangeDueDates = async (uuidSchoolYear) => {
        let url = this._middlewareUrl
        url += '/configuration/base-data'
        url += '/school-years/' + uuidSchoolYear
        url += '/scheduled-school-change-due-dates'

        return axios.get(url)
        .then(response => response.data)
    }
    //#endregion configuration


    // #region customers

    /**
     * Loads the Customer to the ticketholder with the passed uuid
     *
     * @param {string} uuidTicketHolder uuid of the ticketholder
     * @param {string} uuidSchoolYear uuid of the school year
     * @returns {Promise<Customer>} Data for school assignment which was created
     */
    getCustomer = async (uuidTicketHolder, uuidSchoolYear) => {
        let url = this._middlewareUrl
        url += '/ticketholders/' + uuidTicketHolder
        url += '/customer'

        const params = {
            ...uuidSchoolYear && { uuidSchoolYear },
        }

        return axios.get(url, { params: params })
        .then(response => response.data)
    }

    /**
     * Updates the Customer with the passed uuid
     *
     * @param {string} customer new data for the customer
     * @returns {Promise<Customer>} Data for school assignment which was created
     */
    updateCustomer = async (customer) => {
        let url = this._middlewareUrl
        url += '/ticketholders/customers'

        return axios.put(url, customer)
        .then(response => response.data)
    }
    // #endregion customers

    // #region ticket holders
    /**
     * Searches ticketholders with the passed parameters
     *
     * @param {string} uuidCustomer uuid of the customer (in this case it's the corporation, which ist the cost bearer)
     * @param {string} uuidSchoolYear uuid of the school year
     * @param {string} uuidCustomerSchool uuid of the school
     * @param {string} uuidSchoolClass uuid of the school class
     * @returns {Promise<TicketHolderSearchResult[]>} List of ticket holders for this customer or corporation
     */

    getTicketHolders = async (uuidCustomer, uuidSchoolYear, uuidCustomerSchool, uuidSchoolClass) => {
        let url = this._middlewareUrl
        url += '/ticketholders'

        const params = {
            ...uuidCustomer && { uuidCustomer },
            ...uuidSchoolYear && { uuidSchoolYear },
            ...uuidCustomerSchool && { uuidCustomerSchool },
            ...uuidSchoolClass && { uuidSchoolClass }
        }

        return axios.get(url, { params: params })
        .then(response => response.data)
    }

    /**
     * Loads the ticket holder with the passed uuid
     *
     * @param {string} uuidTicketHolder uuid of the ticket holder
     * @param {string} uuidSchoolYear uuid of the school year
     * @returns {Promise<TicketHolder>} Data for school assignment which was created
     */
    getTicketHolder = async (uuidTicketHolder, uuidSchoolYear) => {
        let url = this._middlewareUrl
        url += '/ticketholders/' + uuidTicketHolder

        const params = {
            ...uuidSchoolYear && { uuidSchoolYear },
        }

        return axios.get(url, { params: params })
        .then(response => response.data)
    }

    /**
     * Updates the ticket holder with the passed uuid
     *
     * @param {string} uuidTicketHolder uuid of the ticket holder
     * @param {string} uuidSchoolYear uuid of the school year
     * @param {SchoolAssignment} schoolAssignment school assignment to which the ticket holder belongs to in the defined school year
     * @returns {Promise<TicketHolder>} Data for school assignment which was created
     */
    updateTicketHolder = async (ticketHolder, uuidSchoolYear, schoolAssignment) => {
        let url = this._middlewareUrl
        url += '/ticketholders'

        const params = {
            ...uuidSchoolYear && { uuidSchoolYear },
        }

        ticketHolder.schoolAssignment = schoolAssignment

        return axios.put(url, ticketHolder, { params: params })
        .then(response => response.data)
    }

    /**
     * Creates the ticket holder with the passed data of ticket holder and customer
     *
     * @param {TicketHolder} newTicketHolder uuid of the ticket holder
     * @param {Customer} newCustomer uuid of the ticket holder
     * @param {string} uuidSchoolYear uuid of the school year
     * @param {SchoolAssignment} schoolAssignment school assignment to which the ticket holder belongs to in the defined school year
     * @returns {Promise<TicketHolder>} Data for school assignment which was created
     */
    createTicketHolder = async (newTicketHolder, newCustomer, uuidSchoolYear, schoolAssignment) => {
        let url = this._middlewareUrl
        url += '/ticketholders'

        newTicketHolder.schoolAssignment = schoolAssignment

        const body = {
            newTicketHolder: newTicketHolder,
            newCustomer: newCustomer
        }

        const params = {
            uuidSchoolYear,
        }

        return axios.post(url, body, { params: params })
        .then(response => response.data)
    }

    /**
     *
     * loads the ticket holders school assignement in the passed school year
     *
     * @param {string} uuidTicketHolder id of the ticket holder
     * @param {string} uuidSchoolYear id of the school year
     * @returns {Promise<SchoolAssignment>} object with the school assignment for the ticket holder
     */
    getTicketHolderSchoolAssignment = async (uuidTicketHolder, uuidSchoolYear) => {
        let url = this._middlewareUrl
        url += '/ticketholders/' + uuidTicketHolder
        url += '/school-assignments/' + uuidSchoolYear

        return axios.get(url)
        .then(response => response.data)
    }

    /**
     *
     * deletes the ticket holders school assignement in the passed school year
     *
     * @param {string} uuidTicketHolder id of the ticket holder
     * @param {string} uuidSchoolYear id of the school year
     * @returns {Promise<boolean>} true on success
     */
    deleteTicketHolderSchoolAssignment = async (uuidTicketHolder, uuidSchoolYear) => {
        let url = this._middlewareUrl
        url += '/ticketholders/' + uuidTicketHolder
        url += '/school-assignments/' + uuidSchoolYear

        return axios.delete(url)
        .then(response => response.data)
    }

    /**
     * Assigns in a bulk school and a school class to ticket holders for the corresponding school year
     *
     * @param {string} uuidSchoolYear id of the current school year
     * @param {SchoolAssignmentsBulkProcessing[]} schoolAssignments school assignments with uuids of ticket holder
     * @returns {Promise<SchoolAssignmentsBulkProcessing[]>} Created school assignments
     */
    createSchoolAssignments = async (uuidCorporation, uuidSchoolYear, schoolAssignments) => {
        let url = this._middlewareUrl
        url += '/schools/' + uuidCorporation
        url += '/school-assignments/'

        return axios.post(url, { schoolAssignments }, { params: { uuidSchoolYear } })
        .then(response => {
            return response.data
        })
    }

    getDuplicates = async (firstName, lastName, birthdate, postalCode) => {
        let url = this._middlewareUrl
        url += '/ticketholders/duplicates'

        url = new URL(url)
        url.searchParams.append('firstName', firstName)
        url.searchParams.append('lastName', lastName)
        if (birthdate) url.searchParams.append('birthdate', birthdate)
        if (postalCode) url.searchParams.append('postalCode', postalCode)

        return axios.get(url)
        .then(response => response.data)
    }

    /**
     * Loads a list of tickets of the ticket holder depending on parameters
     *
     * @param {string} uuidTicketHolder Id of the ticket holder
     * @param {boolean} activeOnly Only tickets that are valid today or in future or have not yet been issued are loaded
     * @param {string} uuidSchoolYear id of the school year
     *
     * @returns {Promise<AboTicket[]>} Array of AboTicket or emtpy array
     */
    getAboTickets = async (uuidTicketHolder, activeTicketsOnly, uuidSchoolYear) => {
        let url = this._middlewareUrl
        url += '/ticketholders/' + uuidTicketHolder
        url += `/abotickets?activeOnly=${activeTicketsOnly}`

        const params = {
            ...uuidSchoolYear && { uuidSchoolYear },
        }

        return axios.get(url, { params: params })
        .then(response => response.data)
    }

    /**
     * Create an abo ticket for given ticket holder in given school year
     * @param {string} uuidTicketHolder
     * @param {object} aboTicket
     * @param {string} uuidSchoolYear
     * @returns {Promise<object>} created abo ticket
     */
    createAboTicket = async (uuidTicketHolder, aboTicket, uuidSchoolYear) => {
        let url = this._middlewareUrl
        url += '/ticketholders'
        url += '/' + uuidTicketHolder
        url += '/abotickets'

        const params = {
            uuidSchoolYear
        }

        return axios.post(url, aboTicket, { params })
        .then(response => response.data)
    }

    /**
     * Delete abo ticket
     * @param {string} uuidTicketHolder
     * @param {number} ticketId
     * @param {string} uuidSchoolYear
     * @returns {Promise<any>} ?
     */
    deleteAboTicket = async (uuidTicketHolder, ticketId, uuidSchoolYear) => {
        let url = this._middlewareUrl
        url += '/ticketholders/' + uuidTicketHolder
        url += '/abotickets/' + ticketId

        const params = {
            uuidSchoolYear
        }

        return axios.delete(url, { params })
        .then(response => response.data)
    }

    /**
     * Get termination dates for ticket with ticket id
     * @param {string} uuidTicketHolder
     * @param {number} ticketId
     * @param {string} uuidSchoolYear
     * @returns {Promise<string[]>} List of termination dates
     */
    getTerminationDates = async (uuidTicketHolder, ticketId, uuidSchoolYear) => {
        let url = this._middlewareUrl
        url += '/ticketholders/' + uuidTicketHolder
        url += '/abotickets/' + ticketId
        url += '/termination-dates'

        const params = { uuidSchoolYear }

        return axios.get(url, { params })
        .then((response) => response.data)
    }

    /**
     * Get replacement reasons for ticket with ticket id
     * @param {string} uuidTicketHolder
     * @param {number} ticketId
     * @param {string} uuidSchoolYear
     * @returns {Promise<ReasonsTicketReplacement[]>} List of replacement reasons
     */
    getTicketReplacementReasons = async (uuidTicketHolder, ticketId, uuidSchoolYear) => {
        let url = this._middlewareUrl
        url += '/ticketholders/' + uuidTicketHolder
        url += '/abotickets/' + ticketId
        url += '/replacement-reasons'

        const params = { uuidSchoolYear }

        return axios.get(url, { params })
        .then((response) => response.data)
    }

    /**
     * Terminate ticket
     * @param {string} uuidTicketHolder
     * @param {number} ticketId
     * @param {string} uuidReason
     * @param {string} terminationDate
     * @param {string} uuidSchoolYear
     * @returns {Promise<object>} ticket that was terminated
     */
    terminateTicket = async (uuidTicketHolder, ticketId, uuidReason, terminationDate, uuidSchoolYear) => {
        let url = this._middlewareUrl
        url += '/ticketholders/' + uuidTicketHolder
        url += '/abotickets/' + ticketId
        url += '/terminations'

        const params = { uuidSchoolYear }

        const body = {
            uuidReason,
            terminationDate
        }

        return axios.post(url, body, { params })
        .then((response) => response.data)
    }

    /**
     * Replace an abo ticket
     * @param {number} idAboTicket id of the abo ticket
     * @param {string} uuidTicketHolder uuid of the ticket holder
     * @param {number} idReason id of the reason why the ticket shall be replaced
     * @param {number} fee id of the fee why the ticket shall be replaced
     * @param {string} uuidSchoolYear uuid of the school year
     * @returns {Promise<object>} Abo ticket that has been replaced
     */
    replaceAboTicket = async (idAboTicket, uuidTicketHolder, idReason, fee, uuidSchoolYear) => {
        let url = this._middlewareUrl
        url += '/ticketholders/' + uuidTicketHolder
        url += '/abotickets/' + idAboTicket
        url += '/replacements'

        const params = {
            uuidSchoolYear
        }

        const body = {
            idReason,
            fee
        }

        return axios.post(url, body, { params })
        .then(response => response.data)
    }

    getTemporaryTicket = async (idAboTicket, uuidTicketHolder, uuidSchoolYear) => {
        let url = this._middlewareUrl
        url += '/ticketholders/' + uuidTicketHolder
        url += '/abotickets/' + idAboTicket
        url += '/temporary-ticket'

        const params = {
            uuidSchoolYear
        }

        return axios.get(url, { params })
        .then(response => response.data)
    }

    createScheduledSchoolChangeApplication = async (uuidTicketHolder, scheduledSchoolChangeApplication) => {
        let url = this._middlewareUrl
        url += '/ticketholders/' + uuidTicketHolder
        url += '/scheduled-school-change-applications'

        const body = scheduledSchoolChangeApplication

        return axios.post(url, body)
        .then(response => response.data)
    }

    /**
     * Delete a scheduled school change application from a given ticket holder
     * @param {string} uuidTicketHolder
     * @param {string} id uuid of the scheduled school change application
     * @returns {Promise<boolean>}
     */
    deleteScheduledSchoolChangeApplication = async (uuidTicketHolder, id) => {
        let url = this._middlewareUrl
        url += '/ticketholders/' + uuidTicketHolder
        url += '/scheduled-school-change-applications/' + id

        return axios.delete(url)
        .then(response => response.data)
    }

    /**
     *
     * Loads the scheduled school changes of a ticket holder
     *
     * @param {string} uuidTicketHolder id of the ticket holder
     * @param {string} uuidSchoolYear id of the school year
     * @returns {Promise<SchoolAssignment>} object with the school assignment for the ticket holder
     */
    getTicketHolderScheduledSchoolChangeApplications = async (uuidTicketHolder, uuidSchoolYear) => {
        let url = this._middlewareUrl
        url += '/ticketholders/' + uuidTicketHolder
        url += '/scheduled-school-change-applications'

        const params = { uuidSchoolYear }

        return axios.get(url, { params })
        .then(response => response.data)
    }

    // #endregion ticket holders

    // #region corporations
    getCorporations = async () => {
        let url = this._middlewareUrl
        url += '/corporations'

        return axios.get(url)
        .then(response => response.data)
    }

    getCorporation = async (uuidCorporation) => {
        let url = this._middlewareUrl
        url += '/corporations/' + uuidCorporation

        return axios.get(url)
        .then(response => response.data)
    }
    // #endregion


    // #region schools
    getSchools = async () => {
        let url = this._middlewareUrl
        url += '/schools'

        return axios.get(url)
        .then(response => response.data)
    }

    getSchool = async (uuidCorporation) => {
        let url = this._middlewareUrl
        url += '/schools/' + uuidCorporation

        const response = await axios.get(url)
        return response.data
    }

    getSchoolAuthorities = async () => {
        let url = this._middlewareUrl
        url += '/schools'
        url += '/school-authorities/'

        const response = await axios.get(url)
        return response.data
    }

    getDistricts = async () => {
        let url = this._middlewareUrl
        url += '/schools'
        url += '/districts/'

        const response = await axios.get(url)
        return response.data
    }

    getDefaultCostBearer = async (uuidSchool) => {
        let url = this._middlewareUrl
        url += '/schools/' + uuidSchool
        url += '/default-cost-bearer'

        return axios.get(url)
        .then(response => response.data)
    }

    getSchoolFiles = async (uuidCorporation) => {
        let getSchoolFilesUrl = this._middlewareUrl
        getSchoolFilesUrl += '/companies/' + uuidCorporation
        getSchoolFilesUrl += '/files'

        const response = await axios.get(getSchoolFilesUrl)
        return response.data

    }

    getSchoolFile = async (uuidCorporation, uuidDocument) => {
        let getSchoolFileUrl = this._middlewareUrl
        getSchoolFileUrl += '/companies/' + uuidCorporation
        getSchoolFileUrl += '/files/' + uuidDocument

        const response = await axios.get(getSchoolFileUrl)
        return response.data
    }

    getSchoolAccounting = async (uuidCorporation) => {
        let getSchoolAccountingUrl = this._middlewareUrl
        getSchoolAccountingUrl += '/companies/' + uuidCorporation
        getSchoolAccountingUrl += '/accounting'

        const response = await axios.get(getSchoolAccountingUrl)
        return response.data
    }

    // #endregion schools

    // #region classes
    getClasses = async (uuidCorporation) => {
        let url = this._middlewareUrl
        url += '/schools/' + uuidCorporation
        url += '/classes/'

        const response = await axios.get(url)
        return response.data
    }

    addClass = async (uuidCorporation, classToAdd) => {
        let url = this._middlewareUrl
        url += '/schools/' + uuidCorporation
        url += '/classes/'

        const body = classToAdd

        const response = await axios.post(url, body)
        return response.data
    }

    updateClass = async (uuidCorporation, classToUpdate) => {
        let url = this._middlewareUrl
        url += '/schools/' + uuidCorporation
        url += '/classes/'

        const body = classToUpdate

        const response = await axios.put(url, body)
        return response.data
    }

    deleteClass = async (uuidCorporation, classToDelete) => {
        let url = this._middlewareUrl
        url += '/schools/' + uuidCorporation
        url += '/classes/' + classToDelete?.uuid
        const response = await axios.delete(url)
        return response.data
    }
    // #endregion classes

    //region school assignment change

    /**
     * Loads a list of objects containing the data required for school class to display and perform the school assignment change from the given school year to the next school year
     * @param {string} uuidSchool uuidCustomer of the school
     * @param {string} uuidSchoolYear uuid of the actual school year from which the change is to be made
     * @param {string} uuidNextSchoolYear uuid of the school year following the actual school year
     * @returns {Promise<SchoolAssignmentChangeSchoolClass[]>} New school class
     */
    getSchoolAssignmentChangeSchoolClasses = async (uuidSchool, uuidSchoolYear, uuidNextSchoolYear) => {
        let url = this._middlewareUrl
        url += '/schools/' + uuidSchool
        url += '/school-assignment-change-classes/'

        const params = {
            ...uuidSchoolYear && { uuidSchoolYear },
            ...uuidNextSchoolYear && { uuidNextSchoolYear }
        }

        return axios.get(url, { params: params })
        .then(response => response.data)
    }

    /**
     * Loads an object containing the data required for school class to display and perform the school assignment change from the given school year to the next school year for the given school class
     * @param {string} uuidSchool uuidCustomer of the school
     * @param {string} uuidSchoolYear uuid of the actual school year from which the change is to be made
     * @param {string} uuidNextSchoolYear uuid of the school year following the actual school year
     * @returns {Promise<SchoolAssignmentChangeSchoolClass[]>} New school class
     */
    getSchoolAssignmentChangeSchoolClass = async (uuidSchool, uuidSchoolYear, uuidNextSchoolYear, uuidSchoolClass) => {
        let url = this._middlewareUrl
        url += '/schools/' + uuidSchool
        url += '/school-assignment-change-classes/' + uuidSchoolClass

        const params = {
            ...uuidSchoolYear && { uuidSchoolYear },
            ...uuidNextSchoolYear && { uuidNextSchoolYear }
        }

        return axios.get(url, { params: params })
        .then(response => response.data)
    }

    // #endregion school assignment change

    //#region product variants
    /**
     * Get product variants
     * @param {string} uuidCorporation
     * @returns {Promise<object[]>} list of product variants
     */
    getProductVariants = async (uuidCorporation) => {
        let url = this._middlewareUrl
        url += '/product-variants'

        const params = {
            uuidCorporation
        }

        return axios.get(url, { params })
        .then(response => response.data)
    }

    /**
     * Get product vairant with given id
     * @param {string} productVariantId
     * @returns {Promise<object>} product variant
     */
    getProductVariant = async (productVariantId) => {
        let url = this._middlewareUrl
        url += '/product-variants/' + productVariantId
        return axios.get(url)
        .then(response => response.data)
    }

    /**
     * Get the valid until date of the given product variant
     * @param {string} idProductVariant
     * @param {string} startDate
     * @param {boolean} instantSubscription
     * @returns {Promise<string>} valid until date
     */
    getProductVariantValidUntil = async (idProductVariant, startDate, instantSubscription) => {
        let url = this._middlewareUrl
        url += '/product-variants/' + idProductVariant
        url += '/valid-until'

        const params = {
            startDate,
            instantSubscription
        }

        return await axios.get(url, { params })
        .then(response => response.data?.validUntil)
    }

    /**
     * Get start points for given product variant
     * @param {number} productVariantId id of the product variant
     * @param {string} namePart part of the name of the location
     * @returns {Promise<any[]>} start points
     */
    getStartPoints = async (productVariantId, namePart) => {
        let url = this._middlewareUrl
        url += '/product-variants/' + productVariantId
        url += '/start-points'

        const params = {
            namePart
        }

        return axios.get(url, { params })
        .then(response => response.data)
    }

    /**
     * Get end points for given product
     * @param {number} productVariantId id of the product variant
     * @param {string} namePart part of the name of the location
     * @param {number} startPointNumber number of the start point
     * @returns {Promise<object[]>} list of possible end points
     */
    getEndPoints = async (productVariantId, namePart, idStartPoint) => {
        let url = this._middlewareUrl
        url += '/product-variants/' + productVariantId
        url += '/end-points'

        const params = {
            namePart,
            idStartPoint
        }

        return axios.get(url, { params })
        .then(response => response.data)
    }


    createRelations = async (productVariantId, startPoint, endPoint, startDate) => {
        let url = this._middlewareUrl
        url += '/product-variants/' + productVariantId
        url += '/relations'

        const body = {
            startPoint: startPoint,
            endPoint: endPoint,
            validFrom: startDate
        }

        return axios.post(url, body)
        .then(response => response.data)
    }

    getPriceDetails = async (idProductVariant, priceLevel, validAt, uuidTicketHolder, uuidSchoolYear) => {
        let url = this._middlewareUrl
        url += '/ticketholders/' + uuidTicketHolder
        url += '/product-variants/' + idProductVariant
        url += '/price-details'

        const params = {
            priceLevel,
            validAt,
            uuidSchoolYear
        }

        return axios.get(url, { params })
        .then(response => response.data)
    }
    // #endregion product variants

    //#region applications

    /**
     *
     * @param {string} uuidCorporation
     * @param {APPLICATION_MAILBOX_TYPE} mailboxType
     * @param {string} lastStatusUpdateFrom
     * @param {string} lastStatusUpdateUntil
     * @returns {ApplicationRequests[]}
     */
    getApplications = async (uuidCorporation, mailboxType, lastStatusUpdateFrom, lastStatusUpdateUntil) => {
        let url = this._middlewareUrl
        url += '/corporations/' + uuidCorporation
        url += '/applications'

        const params = {
            mailboxType,
            lastStatusUpdateFrom,
            lastStatusUpdateUntil
        }

        return axios.get(url, { params })
        .then(response => response.data)
    }

    /**
     * Reject application requests in a bulk
     * @param {string} uuidCorporation
     * @param {string[]} ids ids of the applications to reject
     * @returns {Promise<object[]>} List of applications whose status could not be changed
     */
    rejectApplications = async (uuidCorporation, ids) => {
        let url = this._middlewareUrl
        url += '/corporations/' + uuidCorporation
        url += '/applications'

        const body = {
            'reject': ids,
            'accept': []
        }

        return axios.put(url, body)
        .then(response => response.data)
    }

    /**
     * Accept applications in a bulk
     * @param {string} uuidCorporation
     * @param {string[]} ids ids of the applications to accept
     * @returns {Promise<object[]>} List of applications whose status could not be changed
     */
    acceptApplications = async (uuidCorporation, ids) => {
        let url = this._middlewareUrl
        url += '/corporations/' + uuidCorporation
        url += '/applications'

        const body = {
            'accept': ids,
            'reject': []
        }

        return axios.put(url, body)
        .then(response => response.data)
    }

    /**
     * Patch application
     * @param {string} uuidCorporation id of the corporation that submits the application
     * @param {string} id id of the application to patch
     * @param {object} patchData data to patch
     * @returns {ApplicationRequest} Application with patched data
     */
    patchApplication = async (uuidCorporation, id, patchData ) => {
        let url = this._middlewareUrl
        url += '/corporations/' + uuidCorporation
        url += '/applications/' + id

        return axios.patch(url, patchData)
        .then(response => response.data)
    }

    deleteApplication = async (uuidCorporation, id ) => {
        let url = this._middlewareUrl
        url += '/corporations/' + uuidCorporation
        url += '/applications/' + id

        return axios.delete(url)
        .then(response => response.data)
    }
    //#endregion applications

    //#region account
    /**
     * Get Credentials for a given user
     * @param {string} userId keyckloak user id
     * @returns list of credentials for the given user
     */
    getAccountCredentials = async () => {
        let url = this._middlewareUrl
        url += '/account/credentials'

        return axios.get(url)
        .then(response => response.data)
    }

    /**
     *
     * @param {string} userId keycloak user id
     * @param {string[]} requiredActions required actions to be set to the user e.g. 'CONFIGURE_TOTP'
     * @returns true for success
     */
    createAccountRequiredActions = async (requiredActions) => {
        let url = this._middlewareUrl
        url += '/account/required-actions'

        const body = requiredActions

        return axios.post(url, body)
        .then(response => response.data)
    }
    //#endregion account


    //#region users
    handlePasswordChange = async (oldPassword, newPassword) => {
        let passwordChangeUrl = this._middlewareUrl
        passwordChangeUrl += '/users/reset-password'

        const passwordChangeBody = {
            oldPassword: oldPassword,
            newPassword: newPassword
        }

        return axios.post(passwordChangeUrl, passwordChangeBody)
    }

    /**
     * Get users
     * @param {boolean} withGroups if true users will be returned with groups otherwise without
     * @returns {Promise<object[]>} users
     */
    getUsers = async (withGroups) => {
        let url = this._middlewareUrl
        url += '/users'

        const params = {
            'with-groups': withGroups ? true : undefined
        }

        return axios.get(url, { params })
        .then(response => response.data)
    }

    /**
     * Delete the user
     * @param {string} userId
     * @returns {Promise<void>} nothing
     */
    deleteUser = async (userId) => {
        let url = this._middlewareUrl
        url += '/users/' + userId

        return axios.delete(url)
        .then(response => response.data)
    }

    /**
     * Create user
     * @param {object} user
     * @returns {Promise<string>} uuid of the created user
     */
    createUser = async (user) => {
        let url = this._middlewareUrl
        url += '/users'

        const body = {
            user
        }
        return axios.post(url, body)
        .then(response => response.data)
    }

    /**
     * Add user to corporation
     * @param {string} userId
     * @param {string} uuidCorporation
     * @returns {Promise<any>} ?
     */
    addUserToCorporation = async (userId, uuidCorporation) => {
        let url = this._middlewareUrl
        url += '/users/' + userId
        url += '/corporations/' + uuidCorporation

        return axios.put(url)
        .then(response => response.data)
    }

    /**
     * Remove user from corporation
     * @param {string} userId
     * @param {string} uuidCorporation
     * @returns {any} ?
     */
    removeUserFromCorporation = async (userId, uuidCorporation) => {
        let url = this._middlewareUrl
        url += '/users/' + userId
        url += '/corporations/' + uuidCorporation

        return axios.delete(url)
        .then(response => response.data)
    }

    /**
     * Request a reset of user password for given user
     * @param {string} userId
     * @returns {boolean} true for success
     */
    requestResetUserPassword = async (userId) => {
        let url = this._middlewareUrl
        url += '/users/' + userId
        url += '/actions/require-new-password'

        return axios.put(url)
        .then(response => response.data)
    }

    /**
     * Remove a user group from the user
     * @param {string} userId
     * @param {string} groupId
     * @returns {} nothing
     */
    removeGroupFromUser = async (userId, groupId) => {
        let url = this._middlewareUrl
        url += '/users/' + userId
        url += '/groups/' + groupId

        return axios.delete(url)
        .then(response => response.data)
    }

    /**
     * Add a user group to the user
     * @param {string} userId
     * @param {string} groupId
     * @returns {Promise<void>} nothing
     */
    addGroupToUser = async (userId, groupId) => {
        let url = this._middlewareUrl
        url += '/users/' + userId
        url += '/groups/' + groupId

        return axios.put(url)
        .then(response => response.data)
    }

    /**
     * Get groups, groups can be filtered by assigned corporation scope
     * @param {string} [corporationScope=undefined] optionally corporation scope to get only groups with this corporation scope assigned
     * @returns {Promise<object>} groups
     */
    getGroups = async (corporationScope = undefined) => {
        let url = this._middlewareUrl
        url += '/users/available-groups'

        const params = {
            corporationScope
        }

        return axios.get(url, { params })
        .then(response => response.data)
    }
//#endregion users
}


export const middlewareConnector = new MiddlewareConnector()
